import { ArrowRight, Bluetooth, Bot, Cpu, MemoryStick, Tv, type LucideIcon } from "lucide-react";
import tv5Image from "@/assets/products/tv5/home-card.jpg";
import tv5UltraImage from "@/assets/products/tv5-ultra/hero.jpg";
import tv6Image from "@/assets/products/tv6/hero-lifestyle.jpg";
import tv6UltraImage from "@/assets/products/tv6-ultra/hero-lifestyle-clean.jpg";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

type ModelSpecs = {
  chipset: string;
  memory: string;
  android: string;
  bt: boolean;
};

const models = [
  {
    name: "inext TV6 Ultra",
    slug: "inext-tv6-ultra",
    taglineKey: "lineupTv6UltraTagline",
    image: tv6UltraImage,
    imageWidth: 1200,
    imageHeight: 800,
    imagePosition: "object-center",
    specs: { chipset: "H618", memory: "4 / 32 GB", android: "Android 12", bt: true },
    tierRank: 4,
    flagship: true,
  },
  {
    name: "inext TV6",
    slug: "inext-tv6",
    taglineKey: "lineupTv6Tagline",
    image: tv6Image,
    imageWidth: 1200,
    imageHeight: 800,
    imagePosition: "object-center",
    specs: { chipset: "H618", memory: "2 / 16 GB", android: "Android 12", bt: true },
    tierRank: 3,
    flagship: false,
  },
  {
    name: "inext TV5 Ultra",
    slug: "inext-tv5-ultra",
    taglineKey: "lineupTv5UltraTagline",
    image: tv5UltraImage,
    imageWidth: 1200,
    imageHeight: 799,
    imagePosition: "object-center",
    specs: { chipset: "H313", memory: "2 / 16 GB", android: "Android 10", bt: false },
    tierRank: 2,
    flagship: false,
  },
  {
    name: "inext TV5",
    slug: "inext-tv5",
    taglineKey: "lineupTv5Tagline",
    image: tv5Image,
    imageWidth: 900,
    imageHeight: 498,
    imagePosition: "object-center",
    specs: { chipset: "H313", memory: "1 / 8 GB", android: "Android 10", bt: false },
    tierRank: 1,
    flagship: false,
  },
] as const;

type ChipDef = { icon: LucideIcon; label: string };

function buildChips(specs: ModelSpecs): ChipDef[] {
  const chips: ChipDef[] = [
    { icon: Cpu, label: specs.chipset },
    { icon: MemoryStick, label: specs.memory },
    { icon: Bot, label: specs.android },
    { icon: Tv, label: "4K" },
  ];
  if (specs.bt) chips.push({ icon: Bluetooth, label: "BT" });
  return chips;
}

function SpecChips({ chips }: { chips: ChipDef[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((chip) => (
        <span
          key={chip.label}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-muted-foreground"
        >
          <chip.icon aria-hidden className="h-3.5 w-3.5 text-primary" />
          {chip.label}
        </span>
      ))}
    </div>
  );
}

function TierMeter({ rank }: { rank: number }) {
  return (
    <span aria-hidden className="inline-flex shrink-0 gap-[3px]">
      {[1, 2, 3, 4].map((segment) => (
        <span
          key={segment}
          className={
            segment <= rank ? "h-1 w-3 rounded-full bg-primary" : "h-1 w-3 rounded-full bg-border"
          }
        />
      ))}
    </span>
  );
}

export function ProductLineup() {
  const { messages, site, href } = useSite();
  const compare = localizeUrl("/products", site.locale, href);

  return (
    <section id="lineup" className="section-tint border-b border-border/60 py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <p className="text-sm font-medium uppercase tracking-wider text-primary">
              {messages.lineupEyebrow}
            </p>
            <h2 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">
              {messages.lineupTitle}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">{messages.lineupDescription}</p>
          </div>
          <Button asChild size="lg" className="group h-11 w-fit rounded-xl px-5">
            <a href={compare}>
              {messages.lineupCompareAll}
              <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </Button>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {models.map((model) => {
            const url = localizeUrl(`/products/${model.slug}`, site.locale, href);
            const tagline = messages[model.taglineKey];
            const chips = buildChips(model.specs);

            return (
              <a
                key={model.slug}
                href={url}
                className={`group relative flex min-h-full flex-col overflow-hidden rounded-2xl bg-card shadow-[var(--shadow-elevated)] transition-all duration-300 hover:-translate-y-1 hover:shadow-glow ${
                  model.flagship
                    ? "border-2 border-primary/55"
                    : "border border-border hover:border-primary/30"
                }`}
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-muted/30">
                  <img
                    src={model.image}
                    alt={model.name}
                    width={model.imageWidth}
                    height={model.imageHeight}
                    loading="lazy"
                    decoding="async"
                    className={`h-full w-full object-cover ${model.imagePosition} transition-transform duration-700 ease-out group-hover:scale-[1.035]`}
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/25 via-transparent to-transparent" />
                  {model.flagship && (
                    <span className="absolute left-3 top-3 rounded-full bg-primary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground shadow-sm">
                      {messages.lineupFlagshipBadge}
                    </span>
                  )}
                </div>

                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-lg font-semibold tracking-tight text-foreground">
                      {model.name}
                    </h3>
                    <TierMeter rank={model.tierRank} />
                  </div>
                  <p className="mt-1.5 min-h-10 text-sm text-muted-foreground">{tagline}</p>

                  <div className="mt-5">
                    <SpecChips chips={chips} />
                  </div>

                  <div className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-medium text-primary">
                    {messages.learnMore}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
